/**
 * Карточка квартиры: отметка старших, отзывы сотрудников, история броней.
 *
 * Отзыв — мнение одного менеджера (оценка и текст). Отметка «проверенная /
 * ЧС» — решение для всей компании, её ставят старшие и объясняют причиной:
 * иначе через месяц никто не вспомнит, почему квартира в чёрном списке.
 */

import { useCallback, useEffect, useState } from 'react';
import { Ban, BadgeCheck, CalendarPlus, CircleSlash, ExternalLink, Link2, MapPin, Trash2 } from 'lucide-react';
import type { StayBookingDto, StayPropertyDetailsDto, StayPropertyDto, StayPropertyStatus } from '@tw/shared-types';
import { Button } from '@/components/ui/button';
import { Dialog, DialogBody, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { useSession } from '../../state/session.tsx';
import { describeError } from '../../lib/errors.ts';
import { cn } from '@/lib/utils';
import { INPUT, PlatformMark, PropertyThumb, RatingBadge, RatingInput, StatusPill, TEXTAREA } from './bits.tsx';
import { PLATFORM, PROPERTY_STATUS, formatDate, formatMoney, formatRange, nightsLabel, plural } from './meta.ts';

interface Props {
  propertyId: string | null;
  onClose: () => void;
  onChanged: () => void;
  onBook: (p: StayPropertyDto) => void;
  onOpenUrl?: (url: string) => void;
}

const DATE_TIME = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' });

export function PropertyDialog({ propertyId, onClose, onChanged, onBook, onOpenUrl }: Props) {
  const { api, can } = useSession();
  const [p, setP] = useState<StayPropertyDetailsDto | null>(null);
  const [bookings, setBookings] = useState<StayBookingDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [rating, setRating] = useState<number | null>(null);
  const [text, setText] = useState('');
  const [statusNote, setStatusNote] = useState('');
  const [link, setLink] = useState('');
  const [busy, setBusy] = useState(false);
  const moderator = can('stays.property.moderate');

  const load = useCallback(async () => {
    if (!propertyId) return;
    setError(null);
    try {
      const [details, list] = await Promise.all([
        api.stayProperty(propertyId),
        api.stayBookings({ propertyId, limit: 50 }),
      ]);
      setP(details);
      setStatusNote(details.statusNote ?? '');
      setBookings(list.items);
    } catch (err) {
      setError(describeError(err));
    }
  }, [api, propertyId]);

  useEffect(() => {
    setP(null);
    setRating(null);
    setText('');
    setLink('');
    void load();
  }, [load]);

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
      onChanged();
    } catch (err) {
      setError(describeError(err));
    } finally {
      setBusy(false);
    }
  };

  const setStatus = (status: StayPropertyStatus) =>
    act(() => api.updateStayProperty(p!.id, { status, statusNote: statusNote.trim() || null }));

  const open = (url: string) => (onOpenUrl ? onOpenUrl(url) : window.open(url, '_blank'));

  return (
    <Dialog open={propertyId !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl overflow-hidden p-0">
        {!p ? (
          <div className="p-8 text-center text-sm text-muted-foreground">{error ?? 'Загрузка…'}</div>
        ) : (
          <DialogBody className="flex max-h-[85vh] flex-col gap-0 overflow-y-auto p-0">
            {/* ── Шапка с фото ── */}
            <div className="relative">
              <PropertyThumb propertyId={p.id} photoUrl={p.photoUrl} platform={p.platform} className="h-48 w-full" />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent px-6 pb-4 pt-12 text-white">
                <div className="flex items-center gap-2">
                  <PlatformMark platform={p.platform} />
                  <StatusPill status={p.status} className="bg-white/90" />
                </div>
                <DialogTitle className="mt-2 text-[22px] font-semibold leading-tight tracking-[-0.02em] text-white">{p.title}</DialogTitle>
                <p className="mt-1 flex items-center gap-1 text-[13px] text-white/85">
                  <MapPin className="size-3.5" />
                  {[p.address, p.city, p.country].filter(Boolean).join(', ') || 'адрес не указан'}
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-6 px-6 py-5">
              {/* ── Действия ── */}
              <div className="flex flex-wrap items-center gap-2">
                <RatingBadge rating={p.rating} count={p.ratingCount} />
                <span className="text-[12px] text-muted-foreground">
                  · {p.bookingCount > 0 ? `бронировали ${p.bookingCount} ${plural(p.bookingCount, 'раз', 'раза', 'раз')}` : 'ещё не бронировали'}
                </span>
                <div className="ml-auto flex gap-2">
                  {p.url && (
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => open(p.url!)}>
                      <ExternalLink className="size-4" /> На {PLATFORM[p.platform].label}
                    </Button>
                  )}
                  {can('stays.book') && (
                    <Button size="sm" className="rounded-full" onClick={() => onBook(p)}>
                      <CalendarPlus className="size-4" /> Забронировать
                    </Button>
                  )}
                </div>
              </div>

              {error && <p role="alert" className="rounded-xl bg-destructive/10 px-3 py-2 text-[13px] text-destructive">{error}</p>}

              {/* ── Карточка без ссылки (заведена по брони с квитанции) ── */}
              {!p.url && can('stays.book') && (
                <section className="flex flex-col gap-2 rounded-2xl border border-dashed border-border p-4">
                  <p className="text-[13px] text-soft">
                    Карточка заведена по брони, на странице которой не было ссылки на объявление. Ссылка привяжется сама, когда квартиру
                    откроют во вкладке «Браузер», — или вставьте её здесь.
                  </p>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Link2 className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                      <input
                        className={cn(INPUT, 'pl-9')}
                        placeholder={p.platform === 'AIRBNB' ? 'https://www.airbnb.com/rooms/…' : 'https://www.booking.com/hotel/…'}
                        value={link}
                        onChange={(e) => setLink(e.target.value)}
                      />
                    </div>
                    <Button
                      size="sm"
                      className="h-9 rounded-full"
                      disabled={busy || !link.trim()}
                      onClick={() => void act(() => api.updateStayProperty(p.id, { url: link.trim() }))}
                    >
                      Привязать
                    </Button>
                  </div>
                </section>
              )}

              {/* ── Отметка старших ── */}
              {(moderator || p.status !== 'NEUTRAL') && (
                <section className={cn('rounded-2xl border p-4', p.status === 'BLACKLISTED' ? 'border-destructive/30 bg-destructive/5' : p.status === 'VERIFIED' ? 'border-success/30 bg-success/5' : 'border-border')}>
                  <p className="text-[13px] font-semibold text-heading">{PROPERTY_STATUS[p.status].label}</p>
                  {moderator ? (
                    <div className="mt-2.5 flex flex-col gap-2.5">
                      <textarea
                        className={cn(TEXTAREA, 'min-h-[56px]')}
                        placeholder="Почему: «чисто, хозяин на связи» или «грязно, двое жаловались»"
                        value={statusNote}
                        onChange={(e) => setStatusNote(e.target.value)}
                      />
                      <div className="flex flex-wrap gap-2">
                        <Button size="sm" variant={p.status === 'VERIFIED' ? 'default' : 'outline'} className="rounded-full" disabled={busy} onClick={() => void setStatus('VERIFIED')}>
                          <BadgeCheck className="size-4" /> Проверенная
                        </Button>
                        <Button size="sm" variant={p.status === 'NEUTRAL' ? 'secondary' : 'outline'} className="rounded-full" disabled={busy} onClick={() => void setStatus('NEUTRAL')}>
                          <CircleSlash className="size-4" /> Без отметки
                        </Button>
                        <Button size="sm" variant={p.status === 'BLACKLISTED' ? 'destructive' : 'outline'} className="rounded-full" disabled={busy} onClick={() => void setStatus('BLACKLISTED')}>
                          <Ban className="size-4" /> В чёрный список
                        </Button>
                      </div>
                    </div>
                  ) : (
                    p.statusNote && <p className="mt-1 text-[13px] text-soft">{p.statusNote}</p>
                  )}
                </section>
              )}

              {/* ── Отзывы ── */}
              <section className="flex flex-col gap-3">
                <h3 className="text-[15px] font-semibold text-heading">Отзывы сотрудников</h3>
                {can('stays.book') && (
                  <div className="flex flex-col gap-2 rounded-2xl border border-border bg-secondary/30 p-3">
                    <div className="flex items-center justify-between">
                      <RatingInput value={rating} onChange={setRating} />
                      <span className="text-[11px] text-muted-foreground">оценка необязательна</span>
                    </div>
                    <textarea
                      className={cn(TEXTAREA, 'min-h-[60px] bg-card')}
                      placeholder="Как было: чистота, хозяин, шум, район, подвох при заселении"
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                    />
                    <div className="flex justify-end">
                      <Button
                        size="sm"
                        className="rounded-full"
                        disabled={busy || !text.trim()}
                        onClick={() =>
                          void act(async () => {
                            await api.addStayComment(p.id, { rating, text: text.trim() });
                            setText('');
                            setRating(null);
                          })
                        }
                      >
                        Оставить отзыв
                      </Button>
                    </div>
                  </div>
                )}
                {p.comments.length === 0 ? (
                  <p className="text-[13px] text-muted-foreground">Отзывов пока нет.</p>
                ) : (
                  <ul className="flex flex-col gap-2.5">
                    {p.comments.map((c) => (
                      <li key={c.id} className="group rounded-2xl border border-border bg-card px-4 py-3">
                        <div className="flex items-center gap-2 text-[12px]">
                          <span className="font-semibold text-heading">{c.author?.displayName ?? 'Бывший сотрудник'}</span>
                          <span className="text-muted-foreground">{DATE_TIME.format(new Date(c.createdAt))}</span>
                          {c.rating !== null && <RatingBadge rating={c.rating} />}
                          {c.canDelete && (
                            <button
                              type="button"
                              aria-label="Удалить отзыв"
                              className="ml-auto rounded-md border-0 bg-transparent p-1 text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100"
                              onClick={() => void act(() => api.deleteStayComment(c.id))}
                            >
                              <Trash2 className="size-3.5" />
                            </button>
                          )}
                        </div>
                        <p className="mt-1.5 whitespace-pre-wrap text-[13px] leading-relaxed text-foreground">{c.text}</p>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              {/* ── История броней ── */}
              {bookings.length > 0 && (
                <section className="flex flex-col gap-2">
                  <h3 className="text-[15px] font-semibold text-heading">Брони</h3>
                  <ul className="divide-y divide-border/70 rounded-2xl border border-border">
                    {bookings.map((b) => (
                      <li key={b.id} className={cn('flex items-center gap-3 px-4 py-2.5 text-[13px]', b.status === 'CANCELLED' && 'opacity-55')}>
                        <span className="w-44 flex-none text-heading">{formatRange(b.checkIn, b.checkOut)}</span>
                        <span className="w-20 flex-none text-muted-foreground">{nightsLabel(b.nights)}</span>
                        <span className="min-w-0 flex-1 truncate">{b.workerName ?? 'черновик'}</span>
                        <span className="text-muted-foreground">{b.manager?.displayName}</span>
                        <span className="w-24 flex-none text-right tabular-nums">{formatMoney(b.priceMinor, b.currency)}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-[11px] text-muted-foreground">Показаны брони, которые вам видны.</p>
                </section>
              )}

              {moderator && (
                <div className="flex justify-between border-t border-border pt-4 text-[12px] text-muted-foreground">
                  <span>В базе с {formatDate(p.createdAt.slice(0, 10), true)}</span>
                  <button
                    type="button"
                    className="text-destructive hover:underline"
                    onClick={() => {
                      if (!window.confirm('Удалить квартиру из базы вместе с отзывами? Брони останутся.')) return;
                      void act(() => api.deleteStayProperty(p.id)).then(onClose);
                    }}
                  >
                    Удалить из базы
                  </button>
                </div>
              )}
            </div>
          </DialogBody>
        )}
      </DialogContent>
    </Dialog>
  );
}
